import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/take';

/**
 * Service for coordinating the complex workflow of saving service notes and updates
 * Centralizes the multi-step process of updating service data when changes are made
 */
@Injectable()
export class ServiceNoteSaverService {

    constructor() { }

    /**
     * Prepare service object with updated dates and percentage
     * @param servicio - Service object to update
     * @param dates - Object containing all date/time values
     * @param puntosIntermedios - Intermediate points array
     * @param porcentajeAplicado - Calculated percentage
     * @returns Updated service object
     */
    prepareServiceForSave(
        servicio: any,
        dates: {
            inicioDeServicio: any,
            finDeServicio: any,
            regresoAOrigen: any,
            timeI: any,
            timeF: any,
            timeR: any
        },
        puntosIntermedios: any[],
        porcentajeAplicado: number
    ): any {
        const updated = { ...servicio };

        updated.inicioDeServicio = dates.timeI;
        updated.finDeServicio = dates.timeF;
        updated.regresoAOrigen = dates.timeR;
        updated.puntosIntermedios = puntosIntermedios;
        updated.porcentajeAplicado = porcentajeAplicado;

        return updated;
    }

    /**
     * Build date objects for service save operation
     * @param dateParser - DateParserService instance
     * @param dateFields - Object with all date/time field values
     * @returns Object with formatted dates and timestamps
     */
    buildServiceDates(
        dateParser: any,
        dateFields: {
            inicioDeServicioFecha: string,
            horaInicioServicio: string,
            minutosInicioServicio: string,
            finDeServicioFecha: string,
            horaFinServicio: string,
            minutosFinServicio: string,
            regresoAOrigenFecha: string,
            horaRegresoAOrigen: string,
            minutosRegresoAOrigen: string
        }
    ): any {
        const timeI = dateParser.combineDateTime(
            dateFields.inicioDeServicioFecha,
            dateFields.horaInicioServicio,
            dateFields.minutosInicioServicio
        );
        const timeF = dateParser.combineDateTime(
            dateFields.finDeServicioFecha,
            dateFields.horaFinServicio,
            dateFields.minutosFinServicio
        );
        const timeR = dateParser.combineDateTime(
            dateFields.regresoAOrigenFecha,
            dateFields.horaRegresoAOrigen,
            dateFields.minutosRegresoAOrigen
        );

        return {
            inicioDeServicio: `${dateFields.inicioDeServicioFecha} ${dateFields.horaInicioServicio}:${dateFields.minutosInicioServicio}`,
            finDeServicio: `${dateFields.finDeServicioFecha} ${dateFields.horaFinServicio}:${dateFields.minutosFinServicio}`,
            regresoAOrigen: `${dateFields.regresoAOrigenFecha} ${dateFields.horaRegresoAOrigen}:${dateFields.minutosRegresoAOrigen}`,
            timeI,
            timeF,
            timeR
        };
    }

    /**
     * Update confirmacion with kilometros data
     * @param confirmacionesService - ConfirmacionesService instance
     * @param objConfirmacion - Confirmation object
     * @param parametros_confirmacion - Confirmation parameters
     * @param id_servicio_confirmacion - Service ID in confirmation
     * @param confirmacionId - Confirmation ID
     * @returns Promise when updates complete
     */
    updateConfirmacionKilometros(
        confirmacionesService: any,
        objConfirmacion: any,
        parametros_confirmacion: any,
        id_servicio_confirmacion: string,
        confirmacionId: string
    ): Promise<any[]> {
        objConfirmacion.servicios[id_servicio_confirmacion].kilometros_a_cotizar =
            objConfirmacion.kilometros_a_cotizar;
        parametros_confirmacion.kilometrosDeDistancia = parametros_confirmacion.kilometrosDeDistancia / 1;
        objConfirmacion.servicios[id_servicio_confirmacion].parametros.kilometrosDeDistancia =
            parametros_confirmacion.kilometrosDeDistancia;

        return Promise.all([
            confirmacionesService.actualizarKilometrosDeDistancia(objConfirmacion, confirmacionId),
            confirmacionesService.actualizarKilometrosCotizados(objConfirmacion, confirmacionId)
        ]);
    }

    /**
     * Execute all service update operations in sequence
     * @param services - Object containing all required service instances
     * @param data - Object containing all data needed for updates
     * @returns Observable that completes when all updates finish
     */
    executeServiceUpdates(
        services: {
            confirmacionesService: any,
            serviciosService: any,
            viajesService: any
        },
        data: {
            confirmacionId: string,
            formattedChange: string,
            objetoServicio: any,
            noteObject: any,
            idServicio: string
        }
    ): Observable<any> {
        return new Observable(observer => {
            services.confirmacionesService
                .editarRutaTurismoCorpSchool(
                    data.confirmacionId,
                    "notas_cambios",
                    data.formattedChange,
                    "notas_cambios"
                )
                .take(1)
                .subscribe(() => {
                    services.confirmacionesService
                        .actualizarRutaTurismoCorpSchool(data.confirmacionId, data.objetoServicio)
                        .take(1)
                        .subscribe(() => {
                            services.serviciosService
                                .generarNotaCambioServicio(data.noteObject)
                                .then(() => {
                                    services.viajesService
                                        .actualizarServicio(data.objetoServicio, data.idServicio)
                                        .then(() => {
                                            services.serviciosService.desverificarServicioVentas(data.idServicio);
                                            services.serviciosService.desverificarServicioOperaciones(data.idServicio);
                                            observer.next(true);
                                            observer.complete();
                                        });
                                });
                        });
                });
        });
    }

    /**
     * Validate that note is provided
     * @param nota - Note text
     * @returns true if valid, false otherwise
     */
    validateNote(nota: string): boolean {
        return nota !== "" && nota !== null && nota !== undefined;
    }

    /**
     * Show validation error alert
     */
    showValidationError(): void {
        alert("Debes incluir un 'Motivo de Cambio' para guardar la información.");
    }

    /**
     * Show success message
     */
    showSuccessMessage(): void {
        alert('Información del servicio guardada correctamente.');
    }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/business-logic/service-note-saver.service.ts